<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApi } from '@/composables/api'
import type {
  PreviewIncidentReportResponse,
  RelatedIncidentReportDto
} from '@roadfootage/api-client'
import { useShare } from '@/composables/useShare'
import { storeToRefs } from 'pinia'
import { useShareYourFootage } from '@/composables/useShareYourFootage'
import { useUserStore } from '@/stores/user/user.store'
import { formatIncidentType, formatIncidentDateTime } from '@/utils/format'

import PreviewMediaPlayer from '../components/preview-incident/PreviewMediaPlayer.vue'
import PreviewMapCard from '../components/preview-incident/PreviewMapCard.vue'
import PreviewDetailsCard from '../components/preview-incident/PreviewDetailsCard.vue'
import PreviewDescriptionCard from '../components/preview-incident/PreviewDescriptionCard.vue'
import PreviewPurchaseCard from '../components/preview-incident/PreviewPurchaseCard.vue'
import RelatedFootageCard from '../components/preview-incident/RelatedFootageCard.vue'

const route = useRoute()
const { shareIncident } = useShare()
const router = useRouter()
// No per-call Turnstile token: App.vue holds the router-view until the Turnstile challenge has been
// exchanged for an anonymous session, and the Preview endpoint accepts that bearer token.
const { previewApi } = useApi()
// Related footage is a bonus: a failure hides the section instead of showing a toast.
const { previewApi: quietPreviewApi } = useApi({ notifyOnError: false })
const { isLoggedIn, startSharing } = useShareYourFootage()
const { isRestricted } = storeToRefs(useUserStore())

const cameFromMap = window.history.state?.back?.startsWith('/explore') ?? false

const loading = ref(true)
const incidentReport = ref<PreviewIncidentReportResponse | null>(null)
const relatedItems = ref<RelatedIncidentReportDto[]>([])

const pageTitle = computed(() => {
  if (!incidentReport.value) return ''
  const type = formatIncidentType(incidentReport.value.incidentReportType)
  const location =
    incidentReport.value.address?.suburb ??
    incidentReport.value.address?.formattedAddress ??
    'Unknown location'
  return `${type} — ${location}`
})

const pageSubtitle = computed(() =>
  formatIncidentDateTime(
    incidentReport.value?.incidentDateTime,
    incidentReport.value?.incidentTimezone
  )
)

async function fetchIncidentDetails(id: string) {
  loading.value = true
  incidentReport.value = null
  relatedItems.value = []
  try {
    const loaded = await previewApi.previewPreviewIncidentReportById({ id })
    // The visitor opened another report while this one was loading.
    if (route.params.incidentReportId !== id) return
    incidentReport.value = loaded
  } catch (error) {
    console.error('Failed to fetch incident details:', error)
  } finally {
    if (route.params.incidentReportId === id) loading.value = false
  }
}

async function fetchRelated(id: string) {
  try {
    const related = await quietPreviewApi.previewGetRelatedFootageById({ id })
    if (route.params.incidentReportId !== id) return
    relatedItems.value = related.items ?? []
  } catch (error) {
    console.error('Failed to fetch related footage:', error)
  }
}

// Also runs when the visitor follows a related item: the same view is reused with a new id.
watch(
  () => route.params.incidentReportId as string,
  (id) => {
    if (!id) return
    void fetchIncidentDetails(id)
    void fetchRelated(id)
  },
  { immediate: true }
)

// Back from sign-in with ?share=1: start sharing once, and drop the query so a reload does not repeat it.
let shareStarted = false
watch(
  [incidentReport, isLoggedIn],
  ([report, signedIn]) => {
    if (shareStarted || route.query.share !== '1' || !report || !signedIn) return
    shareStarted = true
    void router.replace({ query: {} }).then(() => startSharing(report))
  },
  { immediate: true }
)
</script>

<template>
  <q-page padding class="bg-grey-2">
    <!--
      layout-wide + q-mx-auto sits on a wrapper, NOT on the row.
      q-col-gutter-lg applies margin: -12px to the row, which cancels the 12px col
      padding at the outer edges — giving a clean 16px (page padding) outer gutter.
      Putting q-mx-auto on the row itself overrides the negative margin via !important,
      which defeats this mechanism and adds an extra 12px on each side.
    -->
    <div class="layout-wide q-mx-auto">
      <div class="row q-col-gutter-lg">
        <!-- Title block -->
        <div class="col-12">
          <!-- Main title -->
          <q-skeleton v-if="loading" type="text" width="65%" height="2rem" />
          <h1 v-else class="text-h5 text-weight-bold q-mt-none q-mb-xs">
            {{ pageTitle }}
          </h1>

          <!-- Date / time subtitle -->
          <q-skeleton v-if="loading" type="text" width="40%" height="1rem" />
          <div v-else-if="pageSubtitle" class="text-body2 text-grey-8">
            {{ pageSubtitle }}
          </div>

          <!-- Country / state chips -->
          <q-skeleton v-if="loading" type="text" width="25%" height="1.4rem" class="q-mb-xs" />
          <div
            v-else-if="incidentReport?.address?.country || incidentReport?.address?.state"
            class="row q-gutter-xs q-my-xs"
          >
            <q-chip
              v-if="incidentReport?.address?.country"
              dense
              color="grey-7"
              text-color="white"
              size="sm"
              :label="incidentReport.address.country"
            />
            <q-chip
              v-if="incidentReport?.address?.state"
              dense
              color="primary"
              text-color="white"
              size="sm"
              :label="incidentReport.address.state"
            />
            <q-chip
              dense
              color="grey-7"
              text-color="white"
              size="sm"
              :label="formatIncidentType(incidentReport?.incidentReportType)"
            />
          </div>

          <!-- Share -->
          <div v-if="!loading && incidentReport" class="q-mt-sm">
            <q-btn
              outline
              dense
              size="sm"
              icon="mdi-share-variant"
              label="Share"
              color="primary"
              data-testid="share-incident"
              @click="shareIncident(route.params.incidentReportId as string)"
            />
          </div>

          <!-- Back to map -->
          <div v-if="cameFromMap" class="q-mt-sm">
            <q-btn
              flat
              dense
              size="sm"
              icon="mdi-arrow-left"
              label="Back to map"
              color="primary"
              @click="router.back()"
            />
          </div>
        </div>

        <!-- Media player — col-8 on desktop, full width on mobile -->
        <div class="col-12 col-lg-8">
          <PreviewMediaPlayer v-if="incidentReport" :incident="incidentReport" />
          <q-card v-else>
            <q-responsive :ratio="16 / 9">
              <q-skeleton class="fit" />
            </q-responsive>
          </q-card>
        </div>

        <!-- Map sidebar — col-4 on desktop, full width on mobile -->
        <div class="col-12 col-lg-4">
          <PreviewMapCard v-if="incidentReport" :incident="incidentReport" />
          <q-card v-else>
            <q-skeleton height="300px" />
          </q-card>
        </div>

        <!-- Incident description -->
        <div class="col-12 col-lg-8">
          <PreviewDescriptionCard v-if="incidentReport" :incident="incidentReport" />
          <q-skeleton v-else type="rect" height="120px" />
        </div>

        <!-- Incident details -->
        <div class="col-12 col-lg-4">
          <PreviewDetailsCard v-if="incidentReport" :incident="incidentReport" />
          <q-skeleton v-else type="rect" height="140px" />
        </div>

        <!-- Related footage: hidden when there is none -->
        <div v-if="relatedItems.length > 0" class="col-12">
          <RelatedFootageCard :items="relatedItems" />
        </div>

        <!-- Share your footage of the same incident -->
        <div v-if="incidentReport && !isRestricted" class="col-12">
          <q-card data-testid="share-yours-card">
            <q-card-section class="row items-center q-col-gutter-md">
              <div class="col-12 col-sm">
                <div class="text-subtitle1 text-weight-medium">Have footage of this incident?</div>
                <div v-if="!isLoggedIn" class="text-caption text-grey-7">
                  Sign in to share your footage.
                </div>
              </div>
              <div class="col-12 col-sm-auto">
                <q-btn
                  unelevated
                  color="primary"
                  icon="mdi-video-plus"
                  label="Share yours"
                  data-testid="share-yours"
                  @click="startSharing(incidentReport)"
                />
              </div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Purchase / download -->
        <div class="col-12">
          <PreviewPurchaseCard />
        </div>
      </div>
    </div>
  </q-page>
</template>
